// OFunded prop-firm promo popup · geo-aware.
// Two variants driven by the backend /api/prop-popup response:
//   • variant: "exclusive" → personalised "Exclusive for {country}" headline
//                            + big discount badge + copyable promo code chip
//                            (e.g. UAE / Kenya / France → 25% off · GOAL25)
//   • variant: "generic"  → the original OFunded promo (safe fallback for
//                            countries with no offer configured or when geo
//                            lookup fails / user is on a VPN we can't map)
// Admin can add/edit/remove country offers from the admin panel.
// Backend still hides the popup entirely for banned countries (e.g. Somalia)
// and respects the master enable toggle.
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, ArrowRight, Copy, Check, Sparkles } from "lucide-react";
import { isPaidAdTraffic } from "@/lib/adTraffic";

const API = process.env.REACT_APP_BACKEND_URL;

// Append the promo code to the OFunded CTA URL so it can be picked up
// server-side if their signup flow supports ?promo= params.
const buildCtaUrl = (base, code) => {
  if (!code) return base;
  try {
    const url = new URL(base);
    url.searchParams.set("promo", code);
    return url.toString();
  } catch {
    return base;
  }
};

// Paths where the OFunded promo popup must never appear: admin console
// and dedicated paid-campaign landing pages (the backdrop blocks forms).
const POPUP_HIDDEN_PATHS = ["/admin", "/tradeboost-75-2k", "/vip-bonus-fr-100"];

export default function PropFirmPopup() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // { variant, country_name, offer }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (POPUP_HIDDEN_PATHS.some((p) => window.location.pathname.startsWith(p))) return;
    // Suppress the prop-firm interstitial for paid-ad visitors (Meta/Google/etc.)
    // so ad landing pages stay compliant — no disruptive auto-popup, no
    // unexpected off-site redirect to ofunded.com. Organic visitors still see it.
    if (isPaidAdTraffic()) return;
    if (localStorage.getItem("prop_popup_dismissed")) return;
    if (sessionStorage.getItem("prop_popup_shown")) return;
    let timer;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/prop-popup`);
        const d = await r.json();
        if (!cancelled && d.visible) {
          setData(d);
          timer = setTimeout(() => {
            setOpen(true);
            sessionStorage.setItem("prop_popup_shown", "1");
          }, 6000);
        }
      } catch { /* fail closed: no popup */ }
    })();
    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  if (!open || POPUP_HIDDEN_PATHS.some((p) => location.pathname.startsWith(p))) return null;

  const close = () => setOpen(false);
  const dismissForever = () => {
    localStorage.setItem("prop_popup_dismissed", "1");
    setOpen(false);
  };

  const isExclusive = data?.variant === "exclusive" && data?.offer?.promo_code;
  const offer = data?.offer || null;
  const countryName = data?.country_name || "";

  const copyCode = async () => {
    if (!offer?.promo_code) return;
    try {
      await navigator.clipboard.writeText(offer.promo_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Fallback for browsers without clipboard permission
      const ta = document.createElement("textarea");
      ta.value = offer.promo_code;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch { /* copy fallback failed */ }
      document.body.removeChild(ta);
    }
  };

  const chips = [
    t("comp.misc.propPopup_chip1", "Up to $250K"),
    t("comp.misc.propPopup_chip2", "90% gain share"),
    t("comp.misc.propPopup_chip3", "From $89"),
    t("comp.misc.propPopup_chip4", "Instant funding"),
  ];

  const centChips = [
    t("comp.misc.centPopup_chip1", "$50 minimum"),
    t("comp.misc.centPopup_chip2", "1.0 pip spreads"),
    t("comp.misc.centPopup_chip3", "1:500 leverage"),
    t("comp.misc.centPopup_chip4", "1,200+ instruments"),
  ];
  const CENT_CTA = "https://live.ofinancial.markets/signup?utm_source=popup&utm_medium=site&utm_campaign=cent_launch";

  // Which promo popup is live (admin-selectable). Defaults to the Cent account.
  const popupKind = data?.popup === "prop" ? "prop" : "cent";

  if (popupKind === "cent") {
    return (
      <div
        className="fixed inset-0 z-[95] flex items-center justify-center p-4"
        data-testid="prop-popup"
        role="dialog"
        aria-modal="true"
        aria-label="OFinancial Cent account"
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} data-testid="prop-popup-backdrop" />
        <div className="prop-popup-card relative w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            data-testid="prop-popup-close"
            className="absolute top-3.5 right-3.5 z-10 p-2 rounded-full bg-black/45 text-white/85 hover:bg-black/70 hover:text-white transition-colors duration-200"
          >
            <X size={16} />
          </button>

          <div className="relative h-44 sm:h-52">
            <img src="/images/cent-popup.jpg" alt="OFinancial Cent account" className="w-full h-full object-cover" />
            <div className="prop-popup-fade absolute inset-0" />
            <div
              className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.22em] text-black shadow-lg"
              style={{ background: "linear-gradient(135deg, #f4d76b 0%, #d4af37 55%, #a67c15 100%)" }}
              data-testid="cent-popup-badge"
            >
              <Sparkles size={11} strokeWidth={2.5} />
              {t("comp.misc.centPopup_badge", "New")}
            </div>
          </div>

          <div className="px-6 sm:px-8 pb-7 pt-4">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.24em]" style={{ color: "#d4af37" }}>
              {t("comp.misc.centPopup_eyebrow", "New from OFinancial Markets")}
            </div>
            <h3 className="prop-popup-title mt-2 text-2xl sm:text-[27px] font-display font-bold leading-tight" data-testid="cent-popup-title">
              {t("comp.misc.centPopup_title", "Introducing the Cent account")}
            </h3>
            <p className="prop-popup-body mt-3 text-[13.5px] leading-relaxed font-light">
              {t("comp.misc.centPopup_body", "Trade real markets with cent-sized risk. Fund from just $50, trade in cents (USC), and get authentic spreads, fills and execution while you build your edge.")}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {centChips.map((c) => (
                <span key={c} className="prop-popup-chip px-3 py-1 rounded-full text-[11.5px] font-semibold">
                  {c}
                </span>
              ))}
            </div>

            {/* Dual path · serves both new and existing clients clearly */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href={CENT_CTA}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cent-popup-cta"
                className="group flex flex-col justify-center rounded-2xl px-5 py-3.5 text-black transition-transform duration-200 hover:-translate-y-0.5"
                style={{ background: "linear-gradient(135deg, #e6c860 0%, #d4af37 55%, #b8912b 100%)" }}
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-75">
                  {t("comp.misc.centPopup_newLabel", "New to OFinancial")}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[14px] font-bold leading-tight mt-0.5">
                  {t("comp.misc.centPopup_cta", "Open a Cent account")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>

              <a
                href="https://live.ofinancial.markets/signin?utm_source=popup&utm_medium=site&utm_campaign=cent_launch"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cent-popup-existing"
                className="prop-popup-learn group flex flex-col justify-center rounded-2xl px-5 py-3.5 transition-colors duration-200"
              >
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-70">
                  {t("comp.misc.centPopup_existingLabel", "Already a client")}
                </span>
                <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold leading-tight mt-0.5">
                  {t("comp.misc.centPopup_existingCta", "Add a Cent wallet")} <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            </div>

            <p className="prop-popup-body mt-3 text-[11.5px] leading-relaxed font-light opacity-80">
              {t("comp.misc.centPopup_existingHint", "Existing clients: log in to your Traders Room, open the Wallets tab and choose Request new wallet → Cent Account.")}
            </p>

            <div className="mt-4 flex items-center gap-4">
              <Link
                to="/account-types/cent"
                onClick={close}
                data-testid="cent-popup-learn"
                className="prop-popup-learn-link inline-flex items-center gap-1 text-[12px] font-semibold underline underline-offset-4 transition-colors duration-200"
                style={{ color: "#d4af37" }}
              >
                {t("comp.misc.centPopup_learn", "Learn more")}
              </Link>
              <button
                type="button"
                onClick={dismissForever}
                data-testid="cent-popup-dismiss"
                className="prop-popup-dismiss ml-auto text-[11.5px] transition-colors duration-200 underline underline-offset-4"
              >
                {t("comp.misc.propPopup_dismiss", "Don't show again")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4"
      data-testid="prop-popup"
      role="dialog"
      aria-modal="true"
      aria-label="OFunded prop firm"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} data-testid="prop-popup-backdrop" />
      <div className="prop-popup-card relative w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          data-testid="prop-popup-close"
          className="absolute top-3.5 right-3.5 z-10 p-2 rounded-full bg-black/45 text-white/85 hover:bg-black/70 hover:text-white transition-colors duration-200"
        >
          <X size={16} />
        </button>

        <div className="relative h-44 sm:h-52">
          <img src="/images/ofunded-popup.jpg" alt="OFunded funded trading" className="w-full h-full object-cover" />
          <div className="prop-popup-fade absolute inset-0" />
          {isExclusive && (
            <div
              data-testid="prop-popup-exclusive-badge"
              className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.22em] text-black shadow-lg"
              style={{ background: "linear-gradient(135deg, #f4d76b 0%, #d4af37 55%, #a67c15 100%)" }}
            >
              <Sparkles size={11} strokeWidth={2.5} />
              {t("comp.misc.propPopup_exclusive_label", "Exclusive")} · {countryName}
            </div>
          )}
        </div>

        <div className="px-6 sm:px-8 pb-7 pt-4">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.24em]" style={{ color: "#d4af37" }}>
            {isExclusive
              ? t("comp.misc.propPopup_eyebrow_exclusive", "Limited-time offer")
              : t("comp.misc.propPopup_eyebrow", "From the OFinancial Group")}
          </div>

          {isExclusive ? (
            <>
              <h3 className="prop-popup-title mt-2 text-2xl sm:text-[27px] font-display font-bold leading-tight" data-testid="prop-popup-title-exclusive">
                {t("comp.misc.propPopup_title_exclusive", "Exclusive for {{country}}: {{discount}}% off OFunded", {
                  country: countryName,
                  discount: offer.discount,
                })}
              </h3>
              <p className="mt-3 text-[13.5px] leading-relaxed font-light text-zinc-300">
                {t(
                  "comp.misc.propPopup_body_exclusive",
                  "As a trader based in {{country}}, get {{discount}}% off any OFunded challenge with the code below. Trade up to $250K in simulated capital, keep 90% of the gains, no time limits.",
                  { country: countryName, discount: offer.discount }
                )}
              </p>

              {/* Copyable promo code chip */}
              <div className="mt-4 flex items-stretch gap-2">
                <div
                  className="prop-popup-code flex-1 min-w-0 flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-dashed"
                  style={{ borderColor: "#d4af37" }}
                >
                  <div className="min-w-0">
                    <div className="text-[9.5px] font-semibold uppercase tracking-[0.24em] text-zinc-400">
                      {t("comp.misc.propPopup_code_label", "Promo code")}
                    </div>
                    <div
                      data-testid="prop-popup-promo-code"
                      className="mt-0.5 font-mono text-lg sm:text-xl font-bold tracking-widest truncate"
                      style={{ color: "#e6c860" }}
                    >
                      {offer.promo_code}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={copyCode}
                    data-testid="prop-popup-copy-code"
                    aria-label="Copy promo code"
                    className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[11.5px] font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5"
                    style={{ background: "linear-gradient(135deg, #e6c860 0%, #d4af37 55%, #b8912b 100%)" }}
                  >
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied
                      ? t("comp.misc.propPopup_copied", "Copied")
                      : t("comp.misc.propPopup_copy", "Copy")}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <h3 className="prop-popup-title mt-2 text-2xl sm:text-[27px] font-display font-bold leading-tight">
                {t("comp.misc.propPopup_title", "Trade our capital. Keep up to 90%.")}
              </h3>
              <p className="mt-3 text-[13.5px] leading-relaxed font-light text-zinc-300">
                {t("comp.misc.propPopup_body", "OFunded, our prop trading firm, backs skilled traders with up to $250K in simulated capital. Pass a challenge from just $89 or start instantly with an Instant Funded account. No time limits, no consistency rule, and up to 90% of account gains paid out monthly.")}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {chips.map((c) => (
                  <span key={c} className="prop-popup-chip px-3 py-1 rounded-full text-[11.5px] font-semibold">
                    {c}
                  </span>
                ))}
              </div>
            </>
          )}

          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a
              href={buildCtaUrl("https://ofunded.com", isExclusive ? offer.promo_code : null)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="prop-popup-cta"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full text-[13.5px] font-bold text-black transition-transform duration-200 hover:-translate-y-0.5"
              style={{ background: "linear-gradient(135deg, #e6c860 0%, #d4af37 55%, #b8912b 100%)" }}
            >
              {isExclusive
                ? t("comp.misc.propPopup_cta_exclusive", "Claim on OFunded")
                : t("comp.misc.propPopup_cta", "Get funded")}{" "}
              <ArrowRight size={15} />
            </a>
            <Link
              to="/company-news/ofunded-prop-firm"
              onClick={close}
              data-testid="prop-popup-learn"
              className="prop-popup-learn inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full text-[13.5px] font-semibold transition-colors duration-200"
            >
              {t("comp.misc.propPopup_learn", "Learn more")}
            </Link>
            <button
              type="button"
              onClick={dismissForever}
              data-testid="prop-popup-dismiss"
              className="prop-popup-dismiss sm:ml-auto text-[11.5px] transition-colors duration-200 underline underline-offset-4"
            >
              {t("comp.misc.propPopup_dismiss", "Don't show again")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
